import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {LabeledTextField} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {
    EmailTextField,
    getContinueUrlFromQueryString,
    getSignupErrorMessageByCode,
    getSignupUnderAgeGateErrorMessageByCode,
    PASSWORD_MIN_LENGTH,
    signupUnderAgeGate,
    signupWithPassword,
    UncontrolledUsernameTextField,
} from "@khan/auth-password";
import {
    calculateNextUrl,
    type Referral,
    type Role,
    type Step,
    type UpdateStep,
} from "@khan/auth-shared";
import {FullNameField} from "@khan/auth-signup";
import {preserveMarketingParams, redirect} from "@khan/urls";

import strings from "#auth/strings.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";

import AuthTitle from "../auth-title.tsx";
import ProgressShellActionScreen from "../progress-shell/progress-shell-action-screen.tsx";

type U13Fields<T> = {
    email: T;
    password: T;
    username: T;
};

type O13Fields<T> = {
    email: T;
    password: T;
    firstName: T;
    lastName: T;
};

type AnyFields<T> = O13Fields<T> & U13Fields<T>;
type FieldValues = AnyFields<string | null | undefined>;
type Submission =
    | {
          fields: U13Fields<string>;
          under13: true;
      }
    | {
          fields: O13Fields<string>;
          under13: false;
      };

type OwnProps = {
    // Used by ProgressShell to coordinate which screens to render.
    previousStep: Step;
    birthdate: string;
    classCode?: string;
    continueUrl?: string;
    referral?: Referral;
    role: Role;
    onEmailSignupSuccess?: () => void;
    // This is derived upstream from `birthdate`.
    under13: boolean;
    updateStep: UpdateStep;
};

type Props = OwnProps & // injected by withLocation
    WithLocationProps & // injected by withApollo
    WithApolloProps;

type DefaultProps = {
    previousStep: Props["previousStep"];
};

type State = {
    fields: Partial<FieldValues>;
    isRedirecting: boolean;
    isSubmitting: boolean;
    /**
     * Error message returned by signupWithPassword.
     * @see libs/auth/password/src/password-signup-graphql.ts#getErrorMessageByCode
     */
    errorMessage: React.ReactNode;
    /**
     * Error set holding all text field ids with an active error.
     * Allows the sign up button to be disabled until there are no errors.
     */
    errorSet: Set<string>;
};

const StyledForm = addStyle("form");

class SignupActionIdentifyScreen extends React.Component<Props, State> {
    static defaultProps: DefaultProps = {
        previousStep: "intro",
    };

    state: State = {
        errorMessage: null,
        fields: {},
        isRedirecting: false,
        isSubmitting: false,
        errorSet: new Set(),
    };

    componentDidMount() {
        this._mounted = true;
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    _mounted = false;

    addError: (id: string) => void = (id) => {
        if (this.state.errorSet.has(id)) {
            return;
        }
        this.setState(({errorSet}) => {
            const newSet = new Set(errorSet);
            newSet.add(id);
            return {errorSet: newSet};
        });
    };

    removeError: (id: string) => void = (id) => {
        if (!this.state.errorSet.has(id)) {
            return;
        }
        this.setState(({errorSet}) => {
            const newSet = new Set(errorSet);
            newSet.delete(id);
            return {errorSet: newSet};
        });
    };

    updateFields: (updated: Partial<FieldValues>) => void = (updated) => {
        this.setState({
            fields: {
                ...this.state.fields,
                ...updated,
            },
        });
    };

    handleBackClick: (e: React.SyntheticEvent) => void = (e) => {
        this.props.updateStep(this.props.previousStep);
    };

    handleSubmit: (e: React.ChangeEvent<HTMLInputElement>) => void = (e) => {
        e.preventDefault();
        this.submitForm();
    };

    handleProceedClick: (e: React.SyntheticEvent) => void = (e) => {
        e.preventDefault();
        this.submitForm();
    };

    // An all-or-nothing look at the state of the form -- if this function
    // returns anything but null, we're ready to submit!
    getSubmission(): Submission | null | undefined {
        const {email, firstName, lastName, password, username} =
            this.state.fields;
        const {under13, classCode} = this.props;
        // Break if there is any active error
        if (this.state.errorSet.size) {
            return null;
        }
        // We check for three valid submission types here. There are more
        // succinct ways to write these conditionals but I prefer writing them
        // more explicitly as I have here.
        if (!under13 && email && firstName && lastName && password) {
            // The user is over-13.
            return {
                under13,
                fields: {email, firstName, lastName, password},
            };
        }
        if (under13 && username && password) {
            if (classCode) {
                // The user is under-13 and came bearing class codes. We didn't
                // ask for their parent's email (and would toss it anyway since
                // they're approved immediately upon being added via /join).
                return {fields: {username, password, email: ""}, under13};
            }
            if (email) {
                // The user is under-13 and gave us their parent's email.
                return {fields: {username, password, email}, under13};
            }
        }
        return null;
    }

    doSignup: (submission: Submission, continueUrl: string) => void = (
        submission: Submission,
        continueUrl: string,
    ) => {
        const {birthdate, role, classCode, gqlClient} = this.props;

        // NOTE: We only pass role if it's different from learner. This is made
        // by design as the corresponding mutation doesn't expect the role
        // variable
        if (!submission.under13) {
            // Over age users.
            signupWithPassword(gqlClient, {
                password: submission.fields.password,
                signupCodes: classCode ? [classCode] : undefined,
                birthdate,
                role,
                email: submission.fields.email,
                firstname: submission.fields.firstName,
                lastname: submission.fields.lastName,
            })
                .then((userData) => {
                    calculateNextUrl({
                        gqlClient,
                        location: this.props.location,
                        // @ts-expect-error - TS2322 - Type 'LoginUserData | null | undefined' is not assignable to type 'LoginUserData'.
                        userData: userData.user,
                        loginType: "password",
                        continueUrl: continueUrl,
                        isFirstLogin: true,
                        joinClassrooms: userData.joinClassrooms,
                    }).then((calculatedNextUrl) => {
                        redirect(
                            preserveMarketingParams(
                                calculatedNextUrl,
                                new URLSearchParams(location.search),
                            ),
                        );
                        if (this._mounted) {
                            this.setState({isRedirecting: true});
                        }
                    });
                })
                .catch((error) => {
                    const errorMessage = getSignupErrorMessageByCode(
                        error.message,
                    );
                    if (this._mounted) {
                        this.setState({
                            isSubmitting: false,
                            isRedirecting: false,
                            errorMessage,
                        });
                    }
                });
        } else {
            signupUnderAgeGate(gqlClient, {
                parentEmail: submission.fields.email,
                username: submission.fields.username,
                password: submission.fields.password,
                birthdate,
                signupCodes: classCode ? [classCode] : undefined,
            })
                .then((childUserData) => {
                    if (childUserData) {
                        calculateNextUrl({
                            gqlClient,
                            location: this.props.location,
                            // @ts-expect-error - TS2322 - Type 'LoginUserData | null | undefined' is not assignable to type 'LoginUserData'.
                            userData: childUserData.user,
                            loginType: "password",
                            continueUrl: continueUrl,
                            isFirstLogin: true,
                            // We're expecting a Classroom here, but instead we have a list of StudentLists,
                            // so we have to map over the list and convert each StudentList to a Classroom.
                            joinClassrooms: childUserData.joinClassrooms
                                ? {
                                      ...childUserData.joinClassrooms,
                                      studentLists:
                                          childUserData.joinClassrooms.studentLists
                                              .filter(
                                                  (studentList) =>
                                                      studentList !== null,
                                              )
                                              .map((studentList) => ({
                                                  ...studentList,
                                                  id: studentList?.id || "",
                                                  name: studentList?.name || "",
                                                  cacheId:
                                                      studentList?.cacheId ||
                                                      undefined,
                                                  signupCode:
                                                      studentList?.signupCode ||
                                                      undefined,
                                                  __typename: "Classroom",
                                              })),
                                  }
                                : undefined,
                        }).then((nextUrl) => {
                            redirect(
                                preserveMarketingParams(
                                    nextUrl,
                                    new URLSearchParams(location.search),
                                ),
                            );
                            if (this._mounted) {
                                this.setState({isRedirecting: true});
                            }
                        });
                    }
                })
                .catch((error) => {
                    const errorMessage =
                        getSignupUnderAgeGateErrorMessageByCode(error.message);

                    if (this._mounted) {
                        this.setState({
                            isSubmitting: false,
                            isRedirecting: false,
                            errorMessage,
                        });
                    }
                });
        }

        this.setState({isSubmitting: true});
    };

    submitForm() {
        const submission = this.getSubmission();
        if (submission == null) {
            return;
        }
        const continueUrl =
            getContinueUrlFromQueryString() || this.props.continueUrl || "/";

        this.doSignup(submission, continueUrl);
    }

    maybeRenderServerError(): React.ReactNode {
        const {errorMessage} = this.state;

        if (errorMessage) {
            return (
                <View style={styles.error}>
                    <Body tag="p" testId="error-message">
                        {errorMessage}
                    </Body>
                </View>
            );
        }

        return null;
    }

    renderProceedButtonLabel(): React.ReactNode {
        const {isRedirecting, isSubmitting} = this.state;
        if (isRedirecting) {
            return t`Logging you in...`;
        }
        if (isSubmitting) {
            return t`Signing up...`;
        }
        return t`Sign up`;
    }

    renderEmailFieldLabel(): string {
        const {under13} = this.props;
        if (under13) {
            return t`Your parent or guardian's email`;
        }
        return t`Your email`;
    }

    renderEmailFieldNotice(): string | null | undefined {
        const {under13} = this.props;
        if (under13) {
            return t`We’re excited to get you started, but we need to notify your parent or guardian about your account.`;
        }
        return null;
    }

    setFirstName(firstName: string): void {
        this.updateFields({firstName: firstName});
    }

    setLastName(lastName: string): void {
        this.updateFields({lastName: lastName});
    }

    render(): React.ReactNode {
        const {under13, classCode} = this.props;
        const {isSubmitting} = this.state;
        const hideEmailField = under13 && classCode;
        return (
            <ProgressShellActionScreen
                showBackButton={true}
                onBackClick={this.handleBackClick}
                showProceedButton={true}
                proceedLabel={this.renderProceedButtonLabel()}
                proceedDisabled={!this.getSubmission() || isSubmitting}
                onProceedClick={this.handleProceedClick}
                maxWidth={433}
            >
                <AuthTitle>{t`Sign up`}</AuthTitle>
                {/* @ts-expect-error - TS2322 - Type '(e: ChangeEvent<HTMLInputElement>) => void' is not assignable to type 'FormEventHandler<HTMLFormElement>'. */}
                <StyledForm style={styles.column} onSubmit={this.handleSubmit}>
                    {!hideEmailField && (
                        <React.Fragment>
                            <EmailTextField
                                testId="signup-email"
                                label={this.renderEmailFieldLabel()}
                                description={
                                    this.renderEmailFieldNotice() || undefined
                                }
                                placeholder={t`example@email.com`}
                                onChange={(newValue) =>
                                    this.updateFields({email: newValue})
                                }
                                onValidate={(errorMessage) => {
                                    if (errorMessage) {
                                        this.addError("signup-email");
                                    } else {
                                        this.removeError("signup-email");
                                    }
                                }}
                            />
                            <Strut size={spacing.medium_16} />
                        </React.Fragment>
                    )}
                    {under13 ? (
                        <UncontrolledUsernameTextField
                            testId="signup-username"
                            label={t`Choose a username`}
                            description={t`Use letters and numbers only. For safety, don't use your real name.`}
                            onChange={(newValue) =>
                                this.updateFields({username: newValue})
                            }
                            onValidate={(errorMessage) => {
                                if (errorMessage) {
                                    this.addError("signup-username");
                                } else {
                                    this.removeError("signup-username");
                                }
                            }}
                        />
                    ) : (
                        <FullNameField
                            firstName={this.state.fields.firstName}
                            lastName={this.state.fields.lastName}
                            setFirstName={(val) => this.setFirstName(val)}
                            setLastName={(val) => this.setLastName(val)}
                            onValidateFirstName={(errorMessage) => {
                                if (errorMessage) {
                                    this.addError("signup-first-name");
                                } else {
                                    this.removeError("signup-first-name");
                                }
                            }}
                            onValidateLastName={(errorMessage) => {
                                if (errorMessage) {
                                    this.addError("signup-last-name");
                                } else {
                                    this.removeError("signup-last-name");
                                }
                            }}
                        />
                    )}
                    <Strut size={spacing.medium_16} />
                    <LabeledTextField
                        testId="signup-password"
                        type="password"
                        label={t`Create a password`}
                        description={strings.passwordNotice(
                            PASSWORD_MIN_LENGTH,
                        )}
                        value={this.state.fields.password || ""}
                        onChange={(newValue) =>
                            this.updateFields({password: newValue})
                        }
                        validate={(value) => {
                            if (!this.state.fields.password && !value) {
                                return;
                            }
                            if (value.length === 0) {
                                return t`Please create a password.`;
                            }
                            if (value.length < PASSWORD_MIN_LENGTH) {
                                return strings.invalidPassword(
                                    PASSWORD_MIN_LENGTH,
                                );
                            }
                        }}
                        onValidate={(errorMessage) => {
                            if (errorMessage) {
                                this.addError("signup-password");
                            } else {
                                this.removeError("signup-password");
                            }
                        }}
                    />
                    <Strut size={spacing.medium_16} />
                    {this.maybeRenderServerError()}
                </StyledForm>
            </ProgressShellActionScreen>
        );
    }
}

const styles = StyleSheet.create({
    error: {
        color: color.red,
    },
    column: {
        display: "flex",
        flexDirection: "column",
    },
});

type ExportProps = WithoutApollo<
    WithoutLocation<
        JSX.LibraryManagedAttributes<
            typeof SignupActionIdentifyScreen,
            React.ComponentProps<typeof SignupActionIdentifyScreen>
        >
    >
>;

export default compose(
    withLocation,
    withApollo,
)(SignupActionIdentifyScreen) as React.ComponentType<ExportProps>;
